import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Globe, Layers, Wallet } from 'lucide-react';

import { CountryFlag } from '@/shared/ui';
import type { Offer } from '@psp-aggregator/sdk';

import { getOfferAttributes } from '../../libs/offerAttributes';
import { REGION_META, regionLabelToCode } from '../../libs/regions';
import { OFFER_LEG_KEYS } from '../../model/legs';
import { LegBlock } from '../LegBlock/LegBlock';
import { RegionGlyph } from '../RegionGlyph/RegionGlyph';

export interface OfferGeneralProps {
  legLayout?: 'hidden' | 'line' | 'panel';
  offer: Offer;
  showCost?: boolean;
}

const CodeList: FC<{
  codes: readonly string[];
  label?: (code: string) => string;
  renderIcon: (code: string) => ReactNode;
}> = ({ codes, label, renderIcon }) => (
  <span className="inline-flex flex-wrap gap-x-2 gap-y-1">
    {codes.map((code) => (
      <span key={code} className="inline-flex items-center gap-1">
        {renderIcon(code)}
        {label ? label(code) : code}
      </span>
    ))}
  </span>
);

CodeList.displayName = 'OfferGeneralCodeList';

const Row: FC<{ icon: typeof Globe; label: string; value: ReactNode }> = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="flex items-start gap-2 px-3.5 py-2">
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    <div className="min-w-0">
      <p className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </p>
      <p className="text-sm break-words text-foreground/90">{value}</p>
    </div>
  </div>
);

Row.displayName = 'OfferGeneralRow';

export const OfferGeneral: FC<OfferGeneralProps> = ({
  legLayout = 'line',
  offer,
  showCost = false,
}) => {
  const { t } = useTranslation();
  const attributes = getOfferAttributes(offer);

  return (
    <div>
      <div className="divide-y divide-border/40">
        <Row
          icon={Globe}
          label={t('pages.admin.offers.fields.regions')}
          value={
            attributes.regions.length > 0 ? (
              <CodeList
                codes={attributes.regions}
                label={(code) => {
                  const region = regionLabelToCode(code);

                  return region ? REGION_META[region].abbrev : code;
                }}
                renderIcon={(code) => {
                  const region = regionLabelToCode(code);

                  return region ? <RegionGlyph code={region} /> : null;
                }}
              />
            ) : (
              '—'
            )
          }
        />
        <Row
          icon={Globe}
          label={t('pages.admin.offers.fields.countries')}
          value={
            attributes.countries.length > 0 ? (
              <CodeList
                codes={attributes.countries}
                renderIcon={(code) => <CountryFlag code={code} />}
              />
            ) : (
              '—'
            )
          }
        />
        <Row
          icon={Wallet}
          label={t('pages.admin.offers.fields.currencies')}
          value={
            attributes.currencies.length > 0
              ? attributes.currencies.join(', ')
              : '—'
          }
        />
        <Row
          icon={CreditCard}
          label={t('pages.admin.offers.fields.paymentMethods')}
          value={
            attributes.payment_methods.length > 0
              ? attributes.payment_methods.join(', ')
              : '—'
          }
        />
        {attributes.integration_methods.length > 0 && (
          <Row
            icon={Layers}
            label={t('pages.admin.offers.fields.integrationType')}
            value={attributes.integration_methods.join(', ')}
          />
        )}
      </div>
      {legLayout !== 'hidden' && (
        <div className="mt-3 space-y-3 px-3.5">
          {OFFER_LEG_KEYS.map((leg) => (
            <LegBlock
              key={leg}
              layout={legLayout}
              leg={leg}
              offer={offer}
              showCost={showCost}
            />
          ))}
        </div>
      )}
    </div>
  );
};

OfferGeneral.displayName = 'OfferGeneral';
